import moment from 'moment';

import type { Invoice, PaidFeatureDescription, Plan, Subscription } from 'Backend/billing/types';
import type { BillingUser } from 'Components/Setup/Billing/utils/billing.types';
import type { APICompany, APIUser } from 'Models/models';

import { getCurrentCompanyId, isHasPermission } from 'Lib/Utils';
import VisaIcon from 'Components/UI_Library/Icons/CardSchemes/VISA.svg';
import MastercardIcon from 'Components/UI_Library/Icons/CardSchemes/Mastercard.svg';
import AmericanExpressIcon from 'Components/UI_Library/Icons/CardSchemes/AmericanExpress.svg';
import DinersClubIcon from 'Components/UI_Library/Icons/CardSchemes/DinersClub.svg';
import DiscoverIcon from 'Components/UI_Library/Icons/CardSchemes/Discover.svg';
import JCBIcon from 'Components/UI_Library/Icons/CardSchemes/JCB.svg';
import DefaultCardIcon from 'Components/UI_Library/Icons/BankCard.png';
import Payroll1 from 'Components/Setup/Billing/img/payroll_1.png';
import Batch1 from 'Components/Setup/Billing/img/batch_1.png';
import ExpenseManagement1 from 'Components/Setup/Billing/img/expenseManagement_1.png';
import BillProcessing1 from 'Components/Setup/Billing/img/billProcessing_1.png';
import BrandedConfirmations1 from 'Components/Setup/Billing/img/brandedConfirmations_1.png';
import ApprovalWorkflows1 from 'Components/Setup/Billing/img/approvalWorkflows_1.png';
import AutoReconcile1 from 'Components/Setup/Billing/img/xeroAutoReconcile_1.png';

const getFeatureDetails = () => [
    {
        key: 'approvalWorkflows',
        name: 'Approval workflows',
        tagline: 'Custom approval settings for your company',
        items: [
            'Apply approval policies to payments, expenses and more',
            'Stack multiple policies with specific conditions for flexibility',
            'Scale your internal controls with multi-stage approvals',
            'Easily approve items on your phone via the Paytron mobile app',
        ],
        images: [ApprovalWorkflows1],
    },
    {
        key: 'batchPayments',
        name: 'Batch payment upload',
        tagline: 'Upload CSV files to create batch payments',
        items: [
            "Send batch payments to all Paytron's supported currencies and countries",
            'Push payment batches through a standard or unique payment approval policy',
            "Track the status of the batch and each individual payment until it's complete",
            'Review validation rules on the uploaded payments to prevent errors',
        ],
        images: [Batch1],
    },
    {
        key: 'billProcessing',
        name: 'Bill processing',
        tagline: 'A more efficient way to capture, process and pay your bills',
        items: [
            'Capture all your invoices at a dedicated email address',
            "Use Paytron's AI to scan the invoice and cut down your manual entry time",
            'Use our Line Item Editor to code your invoice line by line (BETA)',
            'Easily pay tens or hundreds of bills in one go',
        ],
        images: [BillProcessing1],
    },
    {
        key: 'brandedConfirmations',
        name: 'Branded confirmations',
        tagline: 'A professional look on your payment confirmations',
        items: [
            'Make it easy for your suppliers to identify your payments from others',
            'Build brand recognition with every payment',
        ],
        images: [BrandedConfirmations1],
    },
    {
        key: 'expenseManagement',
        name: 'Expense management',
        tagline: 'Capture your receipts and push card expenses to your accounting system',
        items: [
            'A super simple to way to stay compliant while you spend',
            'Code all your card expenses and push them to your accounting system',
            'Request and pay reimbursements for spend on personal cards',
        ],
        images: [ExpenseManagement1],
    },
    {
        key: 'payroll',
        name: 'Payroll payments',
        tagline: 'Pay your team via Paytron',
        items: [
            'Full privacy of payroll data with structured permission settings',
            'Schedule pay runs ahead of time to remove pay day stress',
            'Access to the Employment Hero integration',
            'Upload CSV or ABA files exported from other software',
        ],
        images: [Payroll1],
    },
    {
        key: 'xeroAutoReconcile',
        name: 'Xero auto reconcile',
        tagline: 'Pull and push transactions with Xero in real time',
        items: ['Automatically match and reconcile payments to Xero invoices', 'Eliminate bank feeds and manual work'],
        images: [AutoReconcile1],
    },
    {
        key: 'apiAccess',
    },
    {
        key: 'treasuryManagement',
    },
];

export const paidFeatureBreakdown = (subscription: Subscription | undefined, plan: Plan | undefined) => {
    const currentSubscription = subscription?.featureOptIns || {};
    const featureCosts = plan?.featureFees || {};
    const featureDetails = getFeatureDetails();

    const res: PaidFeatureDescription[] = [];

    let totalCost = 0;

    Object.entries(featureCosts).forEach((feature) => {
        const id = feature?.[0];
        const cost = feature?.[1];
        const description = featureDetails?.find((f) => f.key === id);
        const userHas = currentSubscription?.[id];

        if (id && cost && description && userHas) {
            totalCost += Number(cost);
            res.push({
                id,
                cost,
                name: description?.name || '',
                tagline: description?.tagline || '',
                userHas,
            });
        }
    });

    return { cost: totalCost, features: res };
};

export const userBreakdown = (companyUsers: BillingUser[] | undefined, plan: Plan | undefined) => {
    // INSPECT: We check for !Paid and Paid, yet we populate companyUsers with Paid, so freeUsers will always be empty??
    const freeUsers = companyUsers?.filter((u: BillingUser) => !u.Paid);
    const paidUsers = companyUsers?.filter((u: BillingUser) => u.Paid);
    const numPaidUsers = paidUsers?.length || 0;

    const numFreeUsersOnPlan = plan?.platformLimits?.users || 0;
    const numUsersToCharge = numPaidUsers - numFreeUsersOnPlan;

    const feePerUser = plan?.platformFees?.users || 0;

    let additionalUserFees = 0;
    if (numUsersToCharge > 0) additionalUserFees = numUsersToCharge * feePerUser;

    const paidUserList = additionalUserFees > 0 ? paidUsers?.slice(0, numUsersToCharge) : [];

    return { cost: additionalUserFees, paidUsers: paidUserList, freeUsers };
};

export const amountDue = (
    subscription: Subscription | undefined,
    latestInvoice: Invoice | undefined,
    plan: Plan | undefined,
    companyUsers: BillingUser[] | undefined
) => {
    const withinInvoicePeriod = () => {
        const invoiceEndDate = moment(latestInvoice?.date).add(1, 'months').toISOString();
        return new Date().toISOString() < invoiceEndDate;
    };

    const calculatedTotal = () => {
        const planPrice = parseInt(plan?.price || '0');
        const featureCosts = paidFeatureBreakdown(subscription, plan)?.cost;
        const userCosts = userBreakdown(companyUsers, plan)?.cost;
        return planPrice + featureCosts + userCosts;
    };

    if (latestInvoice && withinInvoicePeriod()) {
        if (latestInvoice.status === 'PAID') return 0;
        return latestInvoice.total;
    }

    if (plan?.name === 'Pro') return plan.price;

    return calculatedTotal();
};

export const canManage = (subscription?: Subscription, user?: APIUser, company?: APICompany) => {
    if (
        subscription?.managingTenantId &&
        getCurrentCompanyId() === subscription?.managingTenantId &&
        user?.roleName === 'Owner'
    ) {
        return true;
    }
    if (user?.email?.includes('@paytron.com')) return true;
    if (user?.email?.includes('@ofx.com')) return true;
    if (user && company && !subscription?.managingTenantId) {
        if (user?.roleName === 'Owner' || user?.permissions?.billing?.manage) {
            return true;
        }
    } else if (!subscription?.managingTenantId && isHasPermission('billing', 'manage')) {
        return true;
    }

    return false;
};

export const companyStatusMap = (status: string) => {
    switch (status) {
        case 'PENDING':
        case 'PROCESSING':
            return 'ONBOARDING';
        case 'APPROVED - CARDS ONLY':
            return 'CARDS ONLY';
        case 'APPROVED - NO KYC':
            return 'REFERRAL ONLY';
        case 'APPROVED':
            return 'APPROVED';
        case 'SUPPORT':
            return 'SUPPORT VIEW ONLY';
        default:
            return 'ERROR';
    }
};

export const planMap = (planIdOrName: string) => {
    switch (planIdOrName) {
        case '85209bdc-d8ed-4359-913f-3dce5bb9e22b':
            return 'Referral Only';
        case 'Referral Only':
            return '85209bdc-d8ed-4359-913f-3dce5bb9e22b';
        case '85209bdc-d8ed-4359-913f-3dce5bb9e22y':
            return 'Testing';
        case 'Testing':
            return '85209bdc-d8ed-4359-913f-3dce5bb9e22y';
        case '85209bdc-d8ed-4359-913f-3dce5bb9e22d':
            return 'Lite';
        case 'Lite':
            return '85209bdc-d8ed-4359-913f-3dce5bb9e22d';
        case '85209bdc-d8ed-4359-913f-3dce5bb922ba':
            return 'Business Account';
        case 'Business Account':
            return '85209bdc-d8ed-4359-913f-3dce5bb922ba';
        case '85209bdc-d8ed-4359-913f-3dce5bb9e22e':
            return 'Growth';
        case 'Growth':
            return '85209bdc-d8ed-4359-913f-3dce5bb9e22e';
        case '85209bdc-d8ed-4359-913f-3dce5bb9e23e':
            return 'Full-Suite';
        case 'Full-Suite':
            return '85209bdc-d8ed-4359-913f-3dce5bb9e23e';
        case '85209bdc-d8ed-4359-913f-3dce5bb9e22f':
            return 'Pro';
        case 'Pro':
            return '85209bdc-d8ed-4359-913f-3dce5bb9e22f';
        case '85209bdc-d8ed-4359-913f-3dce5bb9e23f':
            return 'Enterprise';
        case 'Enterprise':
            return '85209bdc-d8ed-4359-913f-3dce5bb9e23f';
        default:
            return 'Plan not found';
    }
};

export const mapPlanName = (planName: string) => {
    if (planName.toUpperCase() === 'FULL-SUITE') return 'Standard';
    if (planName.toUpperCase() === 'ENTERPRISE') return 'Premium';
    return planName;
};

export const mapCardSchemeLogo = (cardScheme: string) => {
    switch (cardScheme) {
        case 'visa':
            return <img alt="Visa" src={VisaIcon} />;
        case 'master':
            return <img alt="Mastercard" src={MastercardIcon} />;
        case 'american_express':
            return <img alt="American Express" src={AmericanExpressIcon} />;
        case 'diners_club':
            return <img alt="Diners Club" src={DinersClubIcon} />;
        case 'discover':
            return <img alt="Discovery" src={DiscoverIcon} />;
        case 'jcb':
            return <img alt="JCB" src={JCBIcon} />;
        default:
            return <img alt="Unknown card scheme" src={DefaultCardIcon} />;
    }
};

export const billingCurrency = (company: APICompany | undefined) => {
    if (company?.country === 'New Zealand') return 'NZD';
    return 'AUD';
};
